import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useQueryClient} from '@github-ui/react-query'
import type {RemoveRelatesToMutationMutation} from '@github-ui/server-persisted-queries/types/removeRelatesToMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {RELATES_TO_CONNECTION_PATH} from '../components/sections/relations-section/relates-to-query-types'
import {invalidateRelayRelatesToConnections, writeConfirmedRelatesTo} from './add-relates-to-mutation'

type RemoveRelatesToVariables = {
  issueId: string
  nameWithOwner: string
  issueNumber: number
}

type RemoveRelatesToOperation = Extract<ConnectionOperation, {type: 'removeFromConnection'}>

function toOptimisticOperation(relatedIssueId: string): RemoveRelatesToOperation {
  return {
    type: 'removeFromConnection',
    connectionPath: RELATES_TO_CONNECTION_PATH,
    nodeId: relatedIssueId,
    counts: [{path: [...RELATES_TO_CONNECTION_PATH, 'totalCount'], delta: -1}],
  }
}

async function removeRelatesToRequest({
  nameWithOwner,
  issueNumber,
  relatedIssueId,
}: RemoveRelatesToVariables & {relatedIssueId: string}): Promise<RemoveRelatesToMutationMutation> {
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relates_to.json`, {
    method: 'DELETE',
    body: {relatedIssueId},
  })
  const result = (await response.json()) as {
    data?: RemoveRelatesToMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message ?? result.data?.removeRelatesTo?.errors[0]?.message
  if (!response.ok || error || !result.data?.removeRelatesTo?.issue) {
    throw new Error(error ?? 'Failed to remove related issue')
  }
  return result.data
}

function optimisticRemoveRelatesTo(store: RecordSourceProxy, issueId: string, relatedIssueId: string) {
  // Only the viewer's issue is reliably in the store; the server response reconciles the symmetric reverse edge.
  const issueRecord = store.get(issueId)
  if (!issueRecord) return

  const connection = issueRecord.getLinkedRecord('relatesTo', {first: 3, ranked: true})
  if (connection) {
    const existingNodes = connection.getLinkedRecords('nodes') || []
    const filteredNodes = existingNodes.filter(node => node?.getDataID() !== relatedIssueId)
    if (filteredNodes.length !== existingNodes.length) {
      connection.setLinkedRecords(filteredNodes, 'nodes')
    }
    // A removal always drops the total by one (even for a target beyond the visible top 3); the server reconciles.
    const currentTotal = (connection.getValue('totalCount') as number) ?? 0
    connection.setValue(Math.max(0, currentTotal - 1), 'totalCount')
  }
}

export function useRemoveRelatesToMutation({issueId, nameWithOwner, issueNumber}: RemoveRelatesToVariables) {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)

  const {mutateAsync} = useConnectionMutation<RemoveRelatesToMutationMutation, Error, Disposable>({
    ownerId: issueId,
    connectionPath: RELATES_TO_CONNECTION_PATH,
    mutationFn: operation =>
      removeRelatesToRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        relatedIssueId: (operation as RemoveRelatesToOperation).nodeId,
      }),
    onMutate: ({operation}) => {
      const relatedIssueId = (operation as RemoveRelatesToOperation).nodeId
      return environment.applyUpdate({
        storeUpdater: store => optimisticRemoveRelatesTo(store, issueId, relatedIssueId),
      })
    },
    onSuccess: async (data, variables, revertRelayStore) => {
      const issue = data.removeRelatesTo?.issue
      try {
        if (issue) {
          await settleConfirmedOperation({
            queryClient,
            targets: [{queryKey: secondaryQueryKey}],
            ownerId: issueId,
            operation: {type: 'setFields', id: issue.id, fields: {topRelatesTo: issue.topRelatesTo}},
          })
        }
        await queryClient.invalidateQueries({queryKey: pickerQueryKey})
        commitLocalUpdate(environment, store => {
          writeConfirmedRelatesTo(store, [data.removeRelatesTo?.issue, data.removeRelatesTo?.relatedIssue])
          invalidateRelayRelatesToConnections(store, [
            issueId,
            (variables.operation as RemoveRelatesToOperation).nodeId,
          ])
        })
      } finally {
        revertRelayStore?.dispose()
      }
    },
    onError: (_error, _variables, revertRelayStore) => revertRelayStore?.dispose(),
  })

  return useCallback(
    (relatedIssueId: string) => mutateAsync({operation: toOptimisticOperation(relatedIssueId)}),
    [mutateAsync],
  )
}
